/**
 * operatorCoaching.ts — MER Algo Footprints v8.6.1
 *
 * Deterministic Operator Coaching Layer.
 *
 * Consumes a history of SessionIntelligenceSummary[] and produces:
 *   - PerformanceTrend[]      — directional trends across metrics over N sessions
 *   - StrengthWeakness[]      — recurring strong and weak signal areas
 *   - MistakePattern[]        — detected repeated mistakes with severity
 *   - AdaptationEffectiveness — whether adaptation is helping or hurting
 *   - OperatorProfile         — behavioral archetype derived from history
 *   - CoachingGuidance        — structured, actionable improvement guidance
 *   - CoachingReport          — complete multi-session coaching digest
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. Pure function — same inputs always produce same outputs
 * 2. No Date.now() — non-semantic (display) timestamps excluded from semantic output
 * 3. Rule-based — every classification traces to named thresholds
 * 4. Conservative — minimum occurrence thresholds prevent spurious patterns
 * 5. Thresholds centralized — COACHING_THRESHOLDS only
 *
 * ── Input source ──────────────────────────────────────────────────────────────
 *   SessionIntelligenceSummary[] — ordered oldest → newest
 *   Each item is the output of buildSessionIntelligenceSummary() (v8.6+)
 */

import type { SessionIntelligenceSummary, DecisionGrade } from './operatorIntelligence';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const COACHING_THRESHOLDS = {
  // Trend analysis
  TREND_WINDOW_MIN:          3,    // min sessions for trend analysis
  TREND_IMPROVING_DELTA:     5.0,  // score improvement ≥ 5 pts = 'improving'
  TREND_DEGRADING_DELTA:    -5.0,  // score drop ≥ 5 pts = 'degrading'

  // Strength / weakness detection
  STRENGTH_WEAKNESS_MIN_SESSIONS: 2,   // must appear in at least 2 sessions
  STRENGTH_WIN_RATE_MIN:     0.65,
  WEAKNESS_WIN_RATE_MAX:     0.40,

  // Mistake pattern detection
  MISTAKE_MIN_OCCURRENCES:   2,    // minimum decision occurrences per session × sessions
  MISTAKE_HIGH_SEVERITY_RATE: 0.40, // ≥ 40% of decisions exhibit this mistake
  MISTAKE_MEDIUM_SEVERITY_RATE: 0.20,

  // Adaptation effectiveness
  IMPROVEMENT_DELTA_MIN:     3.0,  // ≥ 3pt score improvement = 'improving'

  // Operator profile conviction thresholds
  AGGRESSIVE_CONVICTION_MIN: 72,
  CONSERVATIVE_CONVICTION_MAX: 52,

  // Minimum sessions for meaningful coaching
  MIN_SESSIONS_FOR_COACHING: 2,
  MIN_SESSIONS_FOR_PROFILE:  3,
} as const;

const T = COACHING_THRESHOLDS;

// ── Output types ──────────────────────────────────────────────────────────────

export type TrendMetric =
  | 'sessionScore'
  | 'calibration'
  | 'signalQuality'
  | 'trapQuality'
  | 'winRate';

export type TrendDirection = 'improving' | 'flat' | 'degrading';

export interface PerformanceTrend {
  metric:    TrendMetric;
  direction: TrendDirection;
  /** Last value minus first value in the window */
  delta:     number;
  /** Number of sessions analysed */
  window:    number;
  /** First and last values in the window */
  from:      number;
  to:        number;
  reasoning: string;
}

export type StrengthWeaknessType = 'strength' | 'weakness';

export interface StrengthWeakness {
  category:    string;
  type:        StrengthWeaknessType;
  /** How many sessions this appeared in */
  frequency:   number;
  /** Fraction of sessions */
  frequencyRate: number;
  /** Structured context labels */
  contexts:    string[];
  summary:     string;
}

export interface MistakePattern {
  key:         string;
  occurrences: number;
  /** Fraction of all graded decisions */
  rate:        number;
  severity:    'low' | 'medium' | 'high';
  contexts:    string[];
  guidance:    string;
}

export interface AdaptationEffectiveness {
  effectiveness: 'improving' | 'neutral' | 'degrading';
  stability:     'stable' | 'volatile';
  notes:         string[];
}

export interface OperatorProfile {
  strongestEdge:       string;
  weakestArea:         string;
  /** conviction style derived from avg conviction across sessions */
  riskBias:            'aggressive' | 'balanced' | 'conservative';
  calibrationProfile:  'overconfident' | 'underconfident' | 'balanced';
  dominantMistake:     string | null;
  dominantPattern:     string | null;
}

export interface CoachingGuidance {
  /** What's working — maintain and invest further */
  maintain:     string[];
  /** Areas to actively develop */
  improve:      string[];
  /** Behaviors / patterns to consciously avoid */
  avoid:        string[];
  /** Highest-leverage improvement areas */
  focusAreas:   string[];
  /** Supporting observations */
  notes:        string[];
}

export interface CoachingReport {
  symbol:                  string;
  sessionCount:            number;
  trends:                  PerformanceTrend[];
  strengths:               StrengthWeakness[];
  weaknesses:              StrengthWeakness[];
  mistakePatterns:         MistakePattern[];
  adaptationEffectiveness: AdaptationEffectiveness;
  operatorProfile:         OperatorProfile | null;
  guidance:                CoachingGuidance;
  /** Stable reasoning trace */
  reasoning:               string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _round1(v: number): number { return Math.round(v * 10) / 10; }
function _round2(v: number): number { return Math.round(v * 100) / 100; }

function _avg(nums: (number | null)[]): number | null {
  const valid = nums.filter((v): v is number => v !== null);
  return valid.length > 0 ? _round1(valid.reduce((s, v) => s + v, 0) / valid.length) : null;
}

// ── Step 1: Performance trends ────────────────────────────────────────────────

function _extractMetric(s: SessionIntelligenceSummary, metric: TrendMetric): number | null {
  switch (metric) {
    case 'sessionScore':  return s.sessionScore.overall;
    case 'calibration':   return s.sessionScore.components.calibration;
    case 'signalQuality': return s.sessionScore.components.signalClarity;
    case 'trapQuality':   return s.sessionScore.components.trapQuality;
    case 'winRate':       return s.sessionScore.components.winRate;
  }
}

function _buildTrend(
  sessions: SessionIntelligenceSummary[],
  metric:   TrendMetric,
): PerformanceTrend | null {
  const vals = sessions.map(s => _extractMetric(s, metric)).filter((v): v is number => v !== null);
  if (vals.length < T.TREND_WINDOW_MIN) return null;

  const from  = vals[0];
  const to    = vals[vals.length - 1];
  const delta = _round1(to - from);

  const direction: TrendDirection = delta >= T.TREND_IMPROVING_DELTA ? 'improving'
    : delta <= T.TREND_DEGRADING_DELTA ? 'degrading'
    : 'flat';

  return {
    metric, direction, delta, window: vals.length, from, to,
    reasoning: `${metric}: ${from} → ${to} (Δ${delta > 0 ? '+' : ''}${delta}) over ${vals.length} sessions`,
  };
}

function _buildTrends(sessions: SessionIntelligenceSummary[]): PerformanceTrend[] {
  const metrics: TrendMetric[] = ['sessionScore', 'calibration', 'winRate', 'signalQuality', 'trapQuality'];
  return metrics
    .map(m => _buildTrend(sessions, m))
    .filter((t): t is PerformanceTrend => t !== null);
}

// ── Step 2: Strengths and weaknesses ─────────────────────────────────────────

function _buildStrengthsWeaknesses(sessions: SessionIntelligenceSummary[]): StrengthWeakness[] {
  const n = sessions.length;
  const results: StrengthWeakness[] = [];

  // Aggregate attribution layer win rates across all sessions' pattern insights
  const layerWins:   Record<string, number> = {};
  const layerActedOn: Record<string, number> = {};
  const layerSessions: Record<string, number> = {};

  for (const s of sessions) {
    for (const p of s.patternInsights) {
      if (!p.key.startsWith('layer:')) continue;
      const layer = p.key.slice(6);
      layerSessions[layer] = (layerSessions[layer] ?? 0) + 1;
      if (p.winRate !== null) {
        const actedEst = p.frequency;
        layerWins[layer]    = (layerWins[layer]    ?? 0) + p.winRate * actedEst;
        layerActedOn[layer] = (layerActedOn[layer] ?? 0) + actedEst;
      }
    }
  }

  for (const [layer, sessionCount] of Object.entries(layerSessions)) {
    if (sessionCount < T.STRENGTH_WEAKNESS_MIN_SESSIONS) continue;
    const winRate = layerActedOn[layer] > 0 ? _round2(layerWins[layer] / layerActedOn[layer]) : null;
    if (winRate === null) continue;

    if (winRate >= T.STRENGTH_WIN_RATE_MIN) {
      results.push({
        category:      layer,
        type:          'strength',
        frequency:     sessionCount,
        frequencyRate: _round2(sessionCount / n),
        contexts:      [layer],
        summary:       `${layer} signals reliable (${(winRate * 100).toFixed(0)}% win rate across ${sessionCount} sessions)`,
      });
    } else if (winRate <= T.WEAKNESS_WIN_RATE_MAX) {
      results.push({
        category:      layer,
        type:          'weakness',
        frequency:     sessionCount,
        frequencyRate: _round2(sessionCount / n),
        contexts:      [layer],
        summary:       `${layer} signals weak (${(winRate * 100).toFixed(0)}% win rate across ${sessionCount} sessions)`,
      });
    }
  }

  return results.sort((a, b) => b.frequency - a.frequency);
}

// ── Step 3: Mistake patterns ──────────────────────────────────────────────────

function _buildMistakePatterns(sessions: SessionIntelligenceSummary[]): MistakePattern[] {
  const patterns: MistakePattern[] = [];

  // Collect all decision grades across all sessions
  const allGrades: DecisionGrade[] = sessions.flatMap(s => s.decisionGrades);
  const total = allGrades.length;
  if (total === 0) return patterns;

  // Mistake 1: Late losses (structured: timing === 'late' && quality === 'low')
  const lateLosses = allGrades.filter(g => g.timing === 'late' && g.quality === 'low');
  if (lateLosses.length >= T.MISTAKE_MIN_OCCURRENCES) {
    const rate = lateLosses.length / total;
    patterns.push({
      key:         'late_loss_entry',
      occurrences: lateLosses.length,
      rate:        _round2(rate),
      severity:    rate >= T.MISTAKE_HIGH_SEVERITY_RATE ? 'high' : rate >= T.MISTAKE_MEDIUM_SEVERITY_RATE ? 'medium' : 'low',
      contexts:    ['late_timing'],
      guidance:    'Review entry confirmation criteria — entering after key turning points consistently leads to losses',
    });
  }

  // Mistake 2: Overconfident losses (structured: overconfident && loss)
  const overconfLosses = allGrades.filter(g =>
    g.calibrationFit === 'overconfident' &&
    (g.outcomeQuality === 'weak_loss' || g.outcomeQuality === 'strong_loss')
  );
  if (overconfLosses.length >= T.MISTAKE_MIN_OCCURRENCES) {
    const rate = overconfLosses.length / total;
    patterns.push({
      key:         'overconfident_loss',
      occurrences: overconfLosses.length,
      rate:        _round2(rate),
      severity:    rate >= T.MISTAKE_HIGH_SEVERITY_RATE ? 'high' : rate >= T.MISTAKE_MEDIUM_SEVERITY_RATE ? 'medium' : 'low',
      contexts:    ['overconfident'],
      guidance:    'Reduce conviction by 5–10% on setups where structural confirmation is incomplete',
    });
  }

  // Mistake 3: Trap-present losses (structured: primaryDriver === 'trap' && loss)
  const trapLosses = allGrades.filter(g =>
    g.primaryDriver === 'trap' &&
    (g.outcomeQuality === 'weak_loss' || g.outcomeQuality === 'strong_loss')
  );
  if (trapLosses.length >= T.MISTAKE_MIN_OCCURRENCES) {
    const rate = trapLosses.length / total;
    patterns.push({
      key:         'trap_signal_ignored',
      occurrences: trapLosses.length,
      rate:        _round2(rate),
      severity:    rate >= T.MISTAKE_HIGH_SEVERITY_RATE ? 'high' : 'medium',
      contexts:    ['trap_active'],
      guidance:    'Treat active trap signals as hard pause — require additional confirmation before acting',
    });
  }

  // Mistake 4: Ambiguous signal quality losses
  const ambiguousLosses = allGrades.filter(g =>
    g.signalClarity === 'ambiguous' &&
    (g.outcomeQuality === 'weak_loss' || g.outcomeQuality === 'strong_loss')
  );
  if (ambiguousLosses.length >= T.MISTAKE_MIN_OCCURRENCES) {
    const rate = ambiguousLosses.length / total;
    patterns.push({
      key:         'ambiguous_signal_acted',
      occurrences: ambiguousLosses.length,
      rate:        _round2(rate),
      severity:    rate >= T.MISTAKE_MEDIUM_SEVERITY_RATE ? 'medium' : 'low',
      contexts:    ['ambiguous_signal'],
      guidance:    'Wait for single-driver signals to mature into multi-driver convergence before committing',
    });
  }

  return patterns.sort((a, b) => {
    const sevOrder = { high: 3, medium: 2, low: 1 };
    return (sevOrder[b.severity] - sevOrder[a.severity]) || b.occurrences - a.occurrences;
  });
}

// ── Step 4: Adaptation effectiveness ─────────────────────────────────────────

function _buildAdaptationEffectiveness(
  sessions:    SessionIntelligenceSummary[],
  trends:      PerformanceTrend[],
): AdaptationEffectiveness {
  if (sessions.length < T.TREND_WINDOW_MIN) {
    return {
      effectiveness: 'neutral',
      stability:     'stable',
      notes:         [`Insufficient sessions (${sessions.length}) for adaptation effectiveness analysis`],
    };
  }

  const scoreTrend = trends.find(t => t.metric === 'sessionScore');
  const calibTrend = trends.find(t => t.metric === 'calibration');
  const notes:      string[] = [];

  // Effectiveness: is overall score improving?
  let effectiveness: AdaptationEffectiveness['effectiveness'] = 'neutral';
  if (scoreTrend) {
    if (scoreTrend.direction === 'improving') {
      effectiveness = 'improving';
      notes.push(`Session score improved ${scoreTrend.delta > 0 ? '+' : ''}${scoreTrend.delta} pts over ${scoreTrend.window} sessions`);
    } else if (scoreTrend.direction === 'degrading') {
      effectiveness = 'degrading';
      notes.push(`Session score degraded ${scoreTrend.delta} pts — review adaptation parameters`);
    } else {
      notes.push('Session score flat — adaptation has not materially improved or hurt performance');
    }
  }

  if (calibTrend?.direction === 'improving') {
    notes.push('Calibration improving — confidence is better aligned with outcomes');
  } else if (calibTrend?.direction === 'degrading') {
    notes.push('Calibration degrading — conviction is drifting from outcome quality');
  }

  // Stability: are session scores volatile?
  const scoreVals = sessions.map(s => s.sessionScore.overall).filter(v => v > 0);
  let stability: AdaptationEffectiveness['stability'] = 'stable';
  if (scoreVals.length >= 3) {
    const mean     = scoreVals.reduce((a, b) => a + b, 0) / scoreVals.length;
    const variance = scoreVals.reduce((s, v) => s + (v - mean) ** 2, 0) / scoreVals.length;
    const stdDev   = Math.sqrt(variance);
    if (stdDev > 15) {
      stability = 'volatile';
      notes.push(`Score std dev ${_round1(stdDev)} pts — inconsistent session quality`);
    } else {
      notes.push(`Score std dev ${_round1(stdDev)} pts — consistent session quality`);
    }
  }

  return { effectiveness, stability, notes };
}

// ── Step 5: Operator profile ──────────────────────────────────────────────────

function _buildOperatorProfile(
  sessions:         SessionIntelligenceSummary[],
  strengths:        StrengthWeakness[],
  weaknesses:       StrengthWeakness[],
  mistakePatterns:  MistakePattern[],
): OperatorProfile | null {
  if (sessions.length < T.MIN_SESSIONS_FOR_PROFILE) return null;

  // Strongest edge: highest-frequency strength, else dominant attribution layer
  const strongestEdge = strengths[0]?.category
    ?? sessions[sessions.length - 1]?.decisionGrades.filter(g => g.quality === 'high')[0]?.primaryDriver
    ?? 'undetermined';

  // Weakest area
  const weakestArea = weaknesses[0]?.category
    ?? mistakePatterns[0]?.key.replace(/_/g, ' ')
    ?? 'none identified';

  // Risk bias from avg conviction
  const allConvictions = sessions.flatMap(s => s.decisionGrades.map(g => g.conviction));
  const avgConv = allConvictions.length > 0
    ? allConvictions.reduce((a, b) => a + b, 0) / allConvictions.length
    : 60;
  const riskBias: OperatorProfile['riskBias'] =
    avgConv >= T.AGGRESSIVE_CONVICTION_MIN   ? 'aggressive' :
    avgConv <= T.CONSERVATIVE_CONVICTION_MAX ? 'conservative' : 'balanced';

  // Calibration profile from predominant calibration result across all grades
  const allGrades = sessions.flatMap(s => s.decisionGrades);
  const calibCounts = { overconfident: 0, underconfident: 0, well_calibrated: 0 };
  for (const g of allGrades) {
    const k = g.calibrationFit as keyof typeof calibCounts;
    if (k in calibCounts) calibCounts[k]++;
  }
  const calibProfile: OperatorProfile['calibrationProfile'] =
    calibCounts.overconfident  > calibCounts.well_calibrated ? 'overconfident' :
    calibCounts.underconfident > calibCounts.well_calibrated ? 'underconfident' : 'balanced';

  return {
    strongestEdge,
    weakestArea,
    riskBias,
    calibrationProfile: calibProfile,
    dominantMistake:    mistakePatterns[0]?.key.replace(/_/g, ' ') ?? null,
    dominantPattern:    sessions.flatMap(s => s.patternInsights).filter(p => p.reliable)[0]?.key ?? null,
  };
}

// ── Step 6: Coaching guidance ─────────────────────────────────────────────────

function _buildCoachingGuidance(
  trends:           PerformanceTrend[],
  strengths:        StrengthWeakness[],
  weaknesses:       StrengthWeakness[],
  mistakes:         MistakePattern[],
  adaptation:       AdaptationEffectiveness,
  profile:          OperatorProfile | null,
): CoachingGuidance {
  const maintain:    string[] = [];
  const improve:     string[] = [];
  const avoid:       string[] = [];
  const focusAreas:  string[] = [];
  const notes:       string[] = [];

  // Maintain: strengths
  for (const s of strengths.slice(0, 2)) {
    maintain.push(`${s.category} signal quality — performing consistently well (${s.summary})`);
  }

  // Improve: weak areas + degrading trends
  for (const w of weaknesses.slice(0, 2)) {
    improve.push(`${w.category} signal discipline — ${w.summary}`);
  }
  for (const t of trends.filter(t => t.direction === 'degrading').slice(0, 2)) {
    improve.push(`${t.metric} recovery — degraded ${Math.abs(t.delta)} pts over ${t.window} sessions`);
  }

  // Avoid: mistake patterns
  for (const m of mistakes.filter(m => m.severity !== 'low').slice(0, 3)) {
    avoid.push(`${m.key.replace(/_/g, ' ')} — ${m.guidance}`);
  }

  // Focus areas: improving trends to reinforce + highest-severity mistakes
  const improvingTrends = trends.filter(t => t.direction === 'improving');
  if (improvingTrends.length > 0) {
    focusAreas.push(`Reinforce what's driving ${improvingTrends.map(t => t.metric).join(', ')} improvements`);
  }
  const highMistakes = mistakes.filter(m => m.severity === 'high');
  if (highMistakes.length > 0) {
    focusAreas.push(`Priority fix: ${highMistakes.map(m => m.key.replace(/_/g, ' ')).join(', ')}`);
  }

  // Calibration notes from profile
  if (profile?.calibrationProfile === 'overconfident') {
    notes.push('Systematic overconfidence detected — apply a 5–10% conviction reduction in ambiguous setups');
  } else if (profile?.calibrationProfile === 'underconfident') {
    notes.push('Systematic underconfidence detected — trust structural signals more, especially on multi-driver convergence');
  }

  // Adaptation note
  if (adaptation.effectiveness === 'improving') {
    notes.push('Adaptive modifiers are improving outcomes — maintain current governance settings');
  } else if (adaptation.effectiveness === 'degrading') {
    notes.push('Adaptation is not improving outcomes — review governance proposal thresholds');
  }

  if (maintain.length === 0 && improve.length === 0 && avoid.length === 0) {
    notes.push('Accumulate more session data (across at least 3 sessions) to generate specific coaching guidance');
  }

  return { maintain, improve, avoid, focusAreas, notes };
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Build a deterministic multi-session Coaching Report.
 *
 * PURE FUNCTION — no Date.now(), no I/O, no side effects.
 *
 * @param symbol   — symbol (or 'ALL' for multi-symbol aggregation)
 * @param sessions — SessionIntelligenceSummary[], ordered oldest → newest
 */
export function buildCoachingReport(
  symbol:   string,
  sessions: SessionIntelligenceSummary[],
): CoachingReport {
  const n = sessions.length;

  if (n < T.MIN_SESSIONS_FOR_COACHING) {
    const emptyGuidance: CoachingGuidance = {
      maintain: [], improve: [], avoid: [], focusAreas: [],
      notes: [`Need at least ${T.MIN_SESSIONS_FOR_COACHING} sessions for coaching (have ${n})`],
    };
    return {
      symbol, sessionCount: n, trends: [], strengths: [], weaknesses: [],
      mistakePatterns: [], adaptationEffectiveness: { effectiveness: 'neutral', stability: 'stable', notes: [] },
      operatorProfile: null, guidance: emptyGuidance,
      reasoning: [`Insufficient sessions (${n} < ${T.MIN_SESSIONS_FOR_COACHING})`],
    };
  }

  const trends          = _buildTrends(sessions);
  const sw              = _buildStrengthsWeaknesses(sessions);
  const strengths       = sw.filter(s => s.type === 'strength');
  const weaknesses      = sw.filter(s => s.type === 'weakness');
  const mistakePatterns = _buildMistakePatterns(sessions);
  const adaptation      = _buildAdaptationEffectiveness(sessions, trends);
  const profile         = _buildOperatorProfile(sessions, strengths, weaknesses, mistakePatterns);
  const guidance        = _buildCoachingGuidance(trends, strengths, weaknesses, mistakePatterns, adaptation, profile);

  // Aggregate stats for reasoning
  const avgScore    = _avg(sessions.map(s => s.sessionScore.overall));
  const bestGrade   = sessions.reduce((b, s) =>
    (s.sessionScore.overall > b.sessionScore.overall ? s : b)).sessionScore.grade;
  const improvingCount = trends.filter(t => t.direction === 'improving').length;
  const degradingCount = trends.filter(t => t.direction === 'degrading').length;

  const reasoning: string[] = [
    `Sessions: ${n} | Avg score: ${avgScore ?? '—'} | Best grade: ${bestGrade}`,
    `Trends: ${improvingCount} improving, ${degradingCount} degrading, ${trends.length - improvingCount - degradingCount} flat`,
    `Strengths: ${strengths.length} | Weaknesses: ${weaknesses.length} | Mistakes: ${mistakePatterns.length}`,
    `Adaptation: ${adaptation.effectiveness} / ${adaptation.stability}`,
    ...(profile ? [`Profile: ${profile.riskBias} risk, ${profile.calibrationProfile} calibration`] : []),
  ];

  return {
    symbol, sessionCount: n, trends, strengths, weaknesses,
    mistakePatterns, adaptationEffectiveness: adaptation, operatorProfile: profile,
    guidance, reasoning,
  };
}
